/**
 * LotteryPayoutEditor — single source of truth for the Straight / Box / Combo
 * payout multipliers across Pick 3 and Pick 4. Lives behind /api/lottery/admin/settings
 * which is now editable by **admin AND operator** (Option A).
 *
 * Shows a "last edited by" line so it's clear whose move took effect last —
 * the backend audit-logs every save with actor id + role.
 *
 * Usage:
 *   <LotteryPayoutEditor />
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Save, RotateCcw, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PICKS = [3, 4];
const KINDS = [
    { key: 'straight', label: 'Straight', hint: 'Exact-order match' },
    { key: 'box',      label: 'Box',      hint: 'Any-order match' },
    { key: 'combo',    label: 'Combo',    hint: 'Paid per matching permutation' },
    { key: 'trips',    label: 'Trips',    hint: 'All-Triples bulk play (e.g. 5-5-5)' },
];

const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago', month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit',
        }) + ' CT';
    } catch { return iso; }
};

const LotteryPayoutEditor = () => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [original, setOriginal] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/lottery/admin/settings`, { withCredentials: true });
            setSettings(data);
            setOriginal(JSON.parse(JSON.stringify(data)));
            setDirty(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load payouts');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const updatePayout = (pick, key, val) => {
        const k = `payouts_pick${pick}`;
        const cur = settings[k] || {};
        setSettings({
            ...settings,
            [k]: { ...cur, [key]: Number(val) || 0 },
        });
        setDirty(true);
    };

    const save = async () => {
        if (!settings) return;
        setSaving(true);
        try {
            await axios.put(`${API}/lottery/admin/settings`, {
                payouts_pick3: settings.payouts_pick3,
                payouts_pick4: settings.payouts_pick4,
            }, { withCredentials: true });
            toast.success('Combo / Box / Straight payouts saved — applies platform-wide on next ticket settlement');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const revert = () => {
        if (!original) return;
        setSettings(JSON.parse(JSON.stringify(original)));
        setDirty(false);
    };

    if (loading) {
        return (
            <div className="text-center py-10 text-zinc-500" data-testid="lottery-payouts-loading">
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            </div>
        );
    }
    if (!settings) return null;

    const editedBy = settings.last_edited_by;

    return (
        <div className="space-y-4" data-testid="lottery-payout-editor">
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-3">
                <Info className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
                <div className="text-[11px] text-amber-100 leading-snug">
                    These payouts apply <span className="font-bold">platform-wide</span>.
                    A change here affects every storefront. Last save is audit-logged with
                    the actor&apos;s id + role.
                    {editedBy && (
                        <div className="mt-1 text-amber-200/80">
                            Last edited by <span className="font-bold text-amber-100">{editedBy.name || 'unknown'}</span>
                            <span className="text-amber-200/60"> ({editedBy.role}) · {fmtDate(settings.last_edited_at)}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-4">
                <p className="text-xs font-bold text-zinc-300 mb-1">Payout multipliers</p>
                <p className="text-[10px] text-zinc-500 mb-4">
                    Win = wager × multiplier. Combo (a.k.a. &ldquo;ways&rdquo;) is paid PER matching permutation,
                    so it&apos;s typically set equal to or slightly below the Straight rate.
                </p>

                {PICKS.map(pick => {
                    const k = `payouts_pick${pick}`;
                    const p = settings[k] || {};
                    return (
                        <div key={pick} className="mb-4 last:mb-0">
                            <p className="text-[11px] font-bold text-emerald-300 mb-2 uppercase tracking-widest">Pick {pick}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                {KINDS.map(({ key, label, hint }) => (
                                    <div key={key} className="rounded border border-zinc-800 bg-zinc-950 p-3">
                                        <Label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</Label>
                                        <p className="text-[9px] text-zinc-600 mb-2">{hint}</p>
                                        <div className="flex items-center gap-2">
                                            <Input
                                                type="number"
                                                step="0.5"
                                                min="0"
                                                value={p[key] ?? ''}
                                                onChange={(e) => updatePayout(pick, key, e.target.value)}
                                                className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9 font-mono text-base font-semibold"
                                                data-testid={`payout-pick${pick}-${key}`}
                                            />
                                            <span className="text-zinc-500 text-sm">×</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="flex items-center justify-end gap-2">
                <Button variant="outline" onClick={revert} disabled={!dirty || saving}
                    className="border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                    data-testid="lottery-payouts-revert">
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Revert
                </Button>
                <Button onClick={save} disabled={!dirty || saving}
                    className="bg-emerald-400 text-zinc-900 hover:bg-emerald-300 font-bold"
                    data-testid="lottery-payouts-save">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1.5" /> Save payouts</>}
                </Button>
            </div>
        </div>
    );
};

export default LotteryPayoutEditor;
